import { ScrollText, Search, Filter, Calendar } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { ComingSoonWatermark } from '@/components/ComingSoonWatermark';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';

export function AuditPage() {
  const { sectionColor } = useNavigation();

  return (
    <div className="space-y-6">
      <ComingSoonWatermark
        pageKey="audit-trail"
        icon={ScrollText}
        title="Audit Trail Coming Soon"
        description="Complete audit logging with user actions, data changes, and system events will be available soon."
      />
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-3xl font-bold", sectionColor)}>Audit Trail</h1>
          <p className="text-neutral-400 mt-1">
            Complete system audit log and change history
          </p>
        </div>
        <div className="flex space-x-2">
          <button className="btn-outline">
            <Filter size={16} className="mr-2" />
            Filter
          </button>
          <button className="btn-outline">
            <Search size={16} className="mr-2" />
            Search
          </button>
        </div>
      </div>

      {/* Audit Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Total Events</p>
              <p className="text-2xl font-bold text-white mt-1">12,847</p>
            </div>
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <ScrollText className="text-primary-400" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Today</p>
              <p className="text-2xl font-bold text-white mt-1">342</p>
            </div>
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <Calendar className="text-primary-400" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">This Week</p>
              <p className="text-2xl font-bold text-white mt-1">1,834</p>
            </div>
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <Calendar className="text-primary-400" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Active Users</p>
              <p className="text-2xl font-bold text-white mt-1">47</p>
            </div>
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <ScrollText className="text-primary-400" size={24} />
            </div>
          </div>
        </Card>
      </div>

      {/* Audit Log Table */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Recent Activity</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-800">
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Timestamp</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">User</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Action</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Resource</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Details</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">IP Address</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm text-neutral-300">2024-11-07 14:32:15</td>
                <td className="py-3 px-4 text-sm text-white">Sarah Johnson</td>
                <td className="py-3 px-4"><span className="badge-primary">Update</span></td>
                <td className="py-3 px-4 text-sm text-neutral-300">Risk RISK-001</td>
                <td className="py-3 px-4 text-sm text-neutral-400">Changed severity from High to Critical</td>
                <td className="py-3 px-4 text-sm font-mono text-neutral-500">192.168.1.42</td>
              </tr>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm text-neutral-300">2024-11-07 14:28:03</td>
                <td className="py-3 px-4 text-sm text-white">Michael Chen</td>
                <td className="py-3 px-4"><span className="badge-success">Create</span></td>
                <td className="py-3 px-4 text-sm text-neutral-300">Control CTRL-342</td>
                <td className="py-3 px-4 text-sm text-neutral-400">Added new access control policy</td>
                <td className="py-3 px-4 text-sm font-mono text-neutral-500">10.0.0.15</td>
              </tr>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm text-neutral-300">2024-11-07 14:15:47</td>
                <td className="py-3 px-4 text-sm text-white">Emily Rodriguez</td>
                <td className="py-3 px-4"><span className="badge-warning">Acknowledge</span></td>
                <td className="py-3 px-4 text-sm text-neutral-300">Alert ALR-089</td>
                <td className="py-3 px-4 text-sm text-neutral-400">Acknowledged security vulnerability alert</td>
                <td className="py-3 px-4 text-sm font-mono text-neutral-500">172.16.0.88</td>
              </tr>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm text-neutral-300">2024-11-07 13:59:22</td>
                <td className="py-3 px-4 text-sm text-white">David Kim</td>
                <td className="py-3 px-4"><span className="badge-danger">Delete</span></td>
                <td className="py-3 px-4 text-sm text-neutral-300">Risk RISK-087</td>
                <td className="py-3 px-4 text-sm text-neutral-400">Removed obsolete risk entry</td>
                <td className="py-3 px-4 text-sm font-mono text-neutral-500">192.168.1.23</td>
              </tr>
              <tr className="hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm text-neutral-300">2024-11-07 13:45:10</td>
                <td className="py-3 px-4 text-sm text-white">Jessica Park</td>
                <td className="py-3 px-4"><span className="badge-primary">Update</span></td>
                <td className="py-3 px-4 text-sm text-neutral-300">Assessment ASS-045</td>
                <td className="py-3 px-4 text-sm text-neutral-400">Completed quarterly risk assessment</td>
                <td className="py-3 px-4 text-sm font-mono text-neutral-500">10.0.0.92</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
